import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiError, distributePrint, listPrinterProfiles, type Batch, type PrintRun, type PrintRunCreated } from "@/api/client";
import { ErrorBanner, ToolbarButton } from "@/components/ui";
import { answeredAfterKeyCheck } from "@/features/batches/attempts";
import { printRunsKey, usePrintRuns } from "@/features/batches/BatchPrint";
import { attemptKeyFor, forgetAttempt } from "@/features/batches/printAttempt";

// keyReusedMessage from internal/httpapi/print_runs.go.
const KEY_REUSED_ERROR = "this Idempotency-Key was already used for a different print request";

type Outage = { runId: string; spoolIds: string[] };

/** A 503 from distribute means the run was committed but some spools never reached the queue. */
export function queueOutage(error: unknown): Outage | null {
  if (!(error instanceof ApiError) || error.status !== 503) return null;
  const runId = error.details.find((d) => d.field === "print_run_id")?.issue;
  if (!runId) return null;
  return { runId, spoolIds: error.details.filter((d) => d.field === "print_spool_id").map((d) => d.issue ?? "") };
}

const listWords = (words: string[]) =>
  words.length <= 1 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

const fieldClass = "mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

export function StartPrintRun({ batch }: { batch: Batch }) {
  const queryClient = useQueryClient();
  const sizeId = useId();
  const profiles = useQuery({
    queryKey: ["printer-profiles", { archived: false }],
    queryFn: () => listPrinterProfiles({ includeArchived: false }),
  });
  const runs = usePrintRuns(batch.id);
  const [profileId, setProfileId] = useState("");
  const [size, setSize] = useState("");
  const [send, setSend] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [outage, setOutage] = useState<Outage | null>(null);
  const [keyRefused, setKeyRefused] = useState(false);
  const [created, setCreated] = useState<(PrintRunCreated & { replayed: boolean }) | null>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  // isPending lands on the next render; a second click in the same tick must not start a second run.
  const submitting = useRef(false);
  useEffect(() => {
    if (confirming) startButton.current?.focus();
  }, [confirming]);

  const profile = profiles.data?.find((p) => p.id === profileId);
  // An archived or deleted profile would otherwise leave the panel stuck mid-confirm.
  useEffect(() => {
    if (confirming && !profile) setConfirming(false);
  }, [confirming, profile]);
  const sizeValue = size.trim() === "" ? NaN : Number(size);
  const sizeValid = Number.isInteger(sizeValue) && sizeValue >= 1 && sizeValue <= 500;
  const generated = batch.generated_count ?? batch.total_count;
  const existingRuns = runs.data?.length ?? 0;
  // The confirm's "already has N runs" warning is only trustworthy once the runs have loaded.
  const runsKnown = runs.isSuccess;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: printRunsKey(batch.id) });
    await queryClient.invalidateQueries({ queryKey: ["batch", batch.id] });
  };
  const forget = () => forgetAttempt(batch.id);
  const distribute = useMutation({
    mutationFn: () => {
      const body = { printer_profile_id: profileId, sub_batch_size: sizeValue, send: send && !!profile?.ipp_allowed };
      // The same request after a lost answer, even across a reload, replays the first run instead of printing twice.
      return distributePrint(batch.id, body, attemptKeyFor(batch.id, body));
    },
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: async (result) => {
      forget();
      setConfirming(false);
      setCreated(result);
      await refresh();
    },
    onError: async (error) => {
      if (answeredAfterKeyCheck(error)) forget();
      setConfirming(false);
      const found = queueOutage(error);
      setOutage(found);
      const refused = error instanceof ApiError && error.status === 422 && error.message === KEY_REUSED_ERROR;
      setKeyRefused(refused);
      // Only a 4xx surely made nothing; a 5xx, lost connection or reused key may follow a committed run.
      if (refused || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) await refresh();
    },
  });
  const begin = () => {
    distribute.reset();
    setCreated(null);
    setOutage(null);
    setKeyRefused(false);
  };

  return (
    <section className="space-y-3 rounded-md border border-zinc-200 p-3">
      <div>
        <h3 className="text-sm font-semibold">Print</h3>
        <p className="mt-1 text-xs text-zinc-500">
          Splits the generated certificates into spools, in seating order, to download or send to the printer.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Printer profile</span>
          <select
            value={profileId}
            disabled={confirming}
            onChange={(e) => {
              const next = profiles.data?.find((p) => p.id === e.target.value);
              setProfileId(e.target.value);
              setSize(next ? String(next.default_sub_batch_size) : "");
              setSend(false);
              begin();
            }}
            className={fieldClass}
          >
            <option value="">Choose a profile</option>
            {(profiles.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.paper_size})
              </option>
            ))}
          </select>
        </label>
        <div className="text-sm">
          <label htmlFor={sizeId} className="font-medium text-zinc-700">
            Sub-batch size
          </label>
          <input
            id={sizeId}
            type="number"
            min={1}
            max={500}
            step={1}
            value={size}
            disabled={!profile || confirming}
            onChange={(e) => setSize(e.target.value)}
            aria-invalid={profile && !sizeValid ? true : undefined}
            aria-describedby={profile && !sizeValid ? `${sizeId}-problem` : undefined}
            className={fieldClass}
          />
          {profile && !sizeValid ? (
            <p id={`${sizeId}-problem`} className="mt-1 text-xs text-red-700">
              must be a whole number between 1 and 500
            </p>
          ) : null}
        </div>
      </div>
      {profile ? (
        <div className="space-y-1 text-sm">
          <label className="flex items-center gap-2 text-zinc-700">
            <input
              type="checkbox"
              checked={send}
              disabled={!profile.ipp_allowed || confirming}
              onChange={(e) => setSend(e.target.checked)}
            />
            Send to printer
          </label>
          {!profile.ipp_uri ? (
            <p className="text-xs text-zinc-500">
              {profile.name} has no IPP printer address, so its spools are downloaded and printed by hand.
            </p>
          ) : !profile.ipp_allowed ? (
            <p className="text-xs text-zinc-500">
              The server may not contact {profile.name}'s printer until ops add its host to PRINT_IPP_ALLOWED_HOSTS; download the
              spools instead.
            </p>
          ) : null}
        </div>
      ) : null}
      {profiles.isSuccess && profiles.data.length === 0 ? (
        <p className="text-xs text-zinc-500">
          No printer profiles yet.{" "}
          <Link to="/printers" search={{}} className="font-medium text-zinc-950 underline">
            Add one under Printers
          </Link>
        </p>
      ) : null}
      <ErrorBanner error={profiles.error} />
      {runs.isError ? (
        <p className="text-xs text-red-700">
          Couldn't check for earlier print runs.{" "}
          <button
            type="button"
            disabled={runs.isFetching}
            onClick={() => void runs.refetch()}
            className="font-medium text-zinc-950 underline disabled:opacity-50"
          >
            Check again
          </button>
        </p>
      ) : null}
      {!confirming ? (
        <ToolbarButton
          disabled={!profile || !sizeValid || !runsKnown || distribute.isPending}
          onClick={() => {
            begin();
            setConfirming(true);
          }}
        >
          Review print run
        </ToolbarButton>
      ) : null}

      {confirming && profile ? (
        <div role="group" aria-label="Confirm print run" className="space-y-2 rounded-md bg-zinc-50 p-3 text-sm text-zinc-700">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Printer profile: <span className="font-medium">{profile.name}</span>
            </li>
            <li>{sizeValue} certificates per spool</li>
            <li>
              Up to {Math.ceil(generated / sizeValue)} spools for {generated} generated certificates; revoked ones are skipped.
            </li>
            <li>
              {send && profile.ipp_allowed
                ? "Each spool is sent to the printer as soon as it is built."
                : "Nothing is sent to the printer: download each spool and print it by hand."}
            </li>
          </ul>
          {existingRuns > 0 ? (
            <p className="font-medium text-amber-800">
              This batch already has {existingRuns} print {existingRuns === 1 ? "run" : "runs"}; printing again prints every
              certificate again.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <ToolbarButton tone="primary"
              ref={startButton}
              aria-label="Start print run"
              disabled={!runsKnown || distribute.isPending}
              onClick={() => {
                if (submitting.current) return;
                submitting.current = true;
                distribute.mutate();
              }}
            >
              {distribute.isPending ? "Starting..." : "Start print run"}
            </ToolbarButton>
            <ToolbarButton disabled={distribute.isPending} onClick={() => setConfirming(false)}>
              Cancel
            </ToolbarButton>
          </div>
        </div>
      ) : null}

      {created?.replayed ? (
        <p role="status" className="text-sm text-amber-800">
          This print run had already started; nothing new was printed. Check its spools in Print runs below and retry any that failed; start again to create another run.
        </p>
      ) : created ? (
        <p role="status" className="text-sm text-emerald-700">
          Print run started: {created.spool_count} spools, {created.certificate_count} certificates.
          {created.skipped.failed + created.skipped.revoked > 0
            ? ` Skipped ${created.skipped.failed} failed and ${created.skipped.revoked} revoked ${created.skipped.revoked === 1 ? "certificate" : "certificates"}.`
            : ""}
        </p>
      ) : null}
      {outage ? (
        <OutageNotice outage={outage} runs={runs.data ?? []} />
      ) : keyRefused ? (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          A print run may already have started for this batch with different settings. Check Print runs below before starting
          another.
        </p>
      ) : (
        <ErrorBanner error={distribute.error} />
      )}
    </section>
  );
}

function OutageNotice({ outage, runs: items }: { outage: Outage; runs: PrintRun[] }) {
  const index = items.findIndex((run) => run.id === outage.runId);
  const run = items[index];
  const seqs = run
    ? run.spools.filter((s) => outage.spoolIds.includes(s.id)).map((s) => String(s.seq))
    : [];
  const count = outage.spoolIds.length;
  return (
    <div role="alert" className="space-y-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <p>
        {run ? `Print run ${items.length - index}` : "The print run"} was created, but {count} of its{" "}
        {count === 1 ? "spool" : "spools"} could not be queued
        {seqs.length > 0 ? `: ${seqs.length === 1 ? "spool" : "spools"} ${listWords(seqs)}` : ""}.
      </p>
      <p>Press Retry on those spools in Print runs below.</p>
      <p className="font-medium">Don't start another print run: it would print every certificate again.</p>
    </div>
  );
}
